import {
  AppBar,
  Badge,
  Box,
  Button,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { Fragment, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import FollowMenu from '@/js/shared/components/lists/FollowMenu';
import ThreadMenu from '@/js/shared/components/lists/ThreadMenu';
import {
  AuthContext,
  ConferenceContext,
  DiscussionContext,
  MenuContext,
  ThreadContext,
} from '@/store';
import UserAvatar from '@/components/ui/userAvatar';
import { RippleBadge } from '@/components/ripple-badge';
import { ProgramViewGrid } from './info/ProgramViewGrid';
import { findCurrentDayId } from '@/lib/utils/DataFormat';
import { favoriteEvents } from '@shared/utils/dataReducers';

const useStyles = (theme, props = {}) => {
  // Add safety checks for theme structure
  if (!theme || !theme.palette) {
    console.warn('Theme or theme.palette is undefined in VisitorNav');
    return {
      appBar: {
        backgroundColor: '#ffffff !important',
        color: '#000000 !important',
        paddingLeft: '10px !important',
        paddingRight: '10px !important',
        position: 'relative !important',
      },
      menuBg: {
        backgroundColor: '#ffffff',
        borderWidth: '1px !important',
        borderStyle: 'solid !important',
        borderColor: '#e0e0e0 !important',
        boxShadow: '0 0 15px rgb(0, 0, 0, 0.15) !important',
        padding: '5px !important',
        borderRadius: '3px !important',
      },
      peopleButton: {
        backgroundColor: '#ffffff !important',
        width: '26px !important',
        height: '22px !important',
        borderRadius: '3px !important',
        fontSize: '1.4rem !important',
      },
      title: ({ centerHeader }) => ({
        textAlign: 'center !important',
        paddingLeft: centerHeader ? '170px !important' : '0px !important',
        flexGrow: '1 !important',
      }),
      loginLink: {
        backgroundColor: '#e0e0e0 !important',
        padding: '2px 10px !important',
        borderRadius: '4px !important',
      },
      loginLinkText: { marginRight: '5px !important' },
      videoIcon: { color: '#FF0000', position: 'relative !important' },
      videoIconCount: {
        fontWeight: '600 !important',
        fontSize: '12px !important',
      },
      leftButtonPadding: { paddingLeft: '180px !important' },
      draft: ({ centerHeader }) => ({
        position: 'absolute !important',
        top: '0 !important',
        width: '100px !important',
        left: `calc(50% ${centerHeader ? '+ 40px' : '- 50px'}) !important`,
        borderBottomLeftRadius: '4px !important',
        borderBottomRightRadius: '4px !important',
        backgroundColor: '#FF0000 !important',
        color: '#ffffff !important',
        padding: '4px !important',
        fontSize: '0.6rem !important',
        zIndex: '2000 !important',
        textAlign: 'center !important',
        '& span': {
          fontSize: '0.8rem !important',
          display: 'inline-block !important',
          verticalAlign: 'middle !important',
          margin: '0 5px 0 0 !important',
        },
      }),
    };
  }

  return {
    appBar: {
      backgroundColor: `${
        props.navBg || theme.palette.background?.default || '#ffffff'
      } !important`,
      color: `${
        theme.palette.text?.primary || theme.palette.common?.black || '#000000'
      } !important`,
      paddingLeft: '10px !important',
      paddingRight: '10px !important',
      position: 'relative !important',
    },
    menuBg: {
      backgroundColor: `${
        theme.palette.background?.default || '#ffffff'
      } !important`,
      borderWidth: '1px !important',
      borderStyle: 'solid !important',
      borderColor: `${
        theme.palette.background?.shadedown_20 || '#e0e0e0'
      } !important`,
      boxShadow: '0 0 15px rgb(0, 0, 0, 0.15) !important',
      padding: '5px !important',
      borderRadius: '3px !important',
    },
    peopleButton: {
      backgroundColor: `${
        theme.palette.background?.default || '#ffffff'
      } !important`,
      width: '26px !important',
      height: '22px !important',
      borderRadius: '3px !important',
      fontSize: '1.4rem !important',
    },
    title: ({ centerHeader }) => ({
      textAlign: 'center !important',
      paddingLeft: centerHeader ? '170px !important' : '0px !important', // drawerWith + padding
      flexGrow: '1 !important',
    }),
    loginLink: {
      backgroundColor: `${
        theme.palette.background?.shadedown_20 || '#e0e0e0'
      } !important`,
      padding: '2px 10px !important',
      borderRadius: '4px !important',
    },
    loginLinkText: {
      marginRight: '5px !important',
    },
    videoIcon: {
      color: '#FF0000 !important',
      position: 'relative !important',
    },
    videoIconCount: {
      fontWeight: '600 !important',
      fontSize: '12px !important',
    },
    leftButtonPadding: {
      paddingLeft: '180px !important',
    },
    draft: ({ centerHeader }) => ({
      position: 'absolute !important',
      top: '0 !important',
      width: '100px !important',
      left: `calc(50% ${centerHeader ? '+ 40px' : '- 50px'}) !important`,
      borderBottomLeftRadius: '4px !important',
      borderBottomRightRadius: '4px !important',
      backgroundColor: '#FF0000 !important',
      color: '#ffffff !important',
      padding: '4px !important',
      fontSize: '0.6rem !important',
      zIndex: '2000 !important',
      textAlign: 'center !important',
      '& span': {
        fontSize: '0.8rem !important',
        display: 'inline-block !important',
        verticalAlign: 'middle !important',
        margin: '0 5px 0 0 !important',
      },
    }),
  };
};

const VisitorNav = (props) => {
  const location = useLocation();
  const { authState, actions, isModeratorOrSpeaker } = useContext(AuthContext);
  const { threadState } = useContext(ThreadContext);
  const { menuOpen, setMenuOpen } = useContext(MenuContext);
  const { discussionNotifications, viewedByClient } = useContext(
    DiscussionContext
  );
  const {
    conferenceUpdateStepper,
    conferenceState: {
      conferenceDetail,
      conferenceDetail: { programDays: days },
    },
  } = useContext(ConferenceContext);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('xs'), {
    noSsr: true,
  });
  const welcomeStateForModerators =
    conferenceDetail &&
    conferenceDetail.state === 'welcome' &&
    isModeratorOrSpeaker;

  const classes = useStyles(theme, {
    navBg: authState.userLoggedIn ? null : 'transparent',
    centerHeader: menuOpen && !smallScreen,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [programMenuEl, setProgramMenuEl] = useState(null);
  const followMenuRef = useRef();
  const threadMenuRef = useRef();

  const open = Boolean(anchorEl);

  const buttonSize = smallScreen ? 'small' : 'medium';

  const unreadChats = threadState.threads.filter(
    (t) => t.threadVisitors.hasUnread
  ).length;
  const unreadNotifications = viewedByClient
    ? 0
    : discussionNotifications.filter((dn) => !dn.viewed).length;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFollowMenu = (event) => {
    followMenuRef.current.open(event.currentTarget);
  };
  const handleThreadMenu = (event) => {
    threadMenuRef.current.open(event.currentTarget);
  };
  // Used for studio button in navbar, currently disabled
  // const handleContributorMenu = (event) => {
  //   contributorMenuRef.current.open(event.currentTarget);
  // };

  const logout = (e) => {
    e.preventDefault();
    setAnchorEl(null);
    actions.logout();
  };

  const account = (e) => {
    e.preventDefault();
    setAnchorEl(null);
    window.location.href = `/profile/account`;
  };
  const editProfile = (e) => {
    e.preventDefault();
    setAnchorEl(null);
    window.location.href = `/profile/edit`;
  };
  const editProfileNotifications = (e) => {
    e.preventDefault();
    setAnchorEl(null);
    window.location.href = `/profile/notifications`;
  };

  // Count how many rooms are streaming
  const currentLiveCount =
    conferenceDetail.programDays.length > 0
      ? conferenceDetail.programDays[0].programRooms.filter(
          (room) => !!room.currentEventId
        ).length
      : 0;

  // if (!conferenceDetail || days.length === 0) {
  //   console.log('No active days, so dont render the program');
  //   return null; // Don't render with 0 active days
  // }

  // const [currentTab, setCurrentTab] = useState(
  //   findCurrentDayId(conferenceDetail)
  // );
  const currentDayId = findCurrentDayId(conferenceDetail);
  const totalFavorites = favoriteEvents(conferenceDetail, currentDayId);

  const currentDay = useMemo(() => {
    return days.find((d) => d.id === currentDayId);
  }, [currentDayId, conferenceUpdateStepper]);

  return (
    <AppBar position="static" sx={classes.appBar}>
      <Toolbar variant="dense" disableGutters>
        {!menuOpen && (
          <IconButton
            size="small"
            edge="start"
            color="default"
            aria-label="Menu open"
            onClick={() => setMenuOpen(true)}
          >
            <Icon>menu</Icon>
          </IconButton>
        )}
        {/* {authState.userLoggedIn && ( */}
        <Box
          display="flex"
          sx={{
            ...(menuOpen && !smallScreen && classes.leftButtonPadding),
          }}
        >
          {welcomeStateForModerators && (
            <Box sx={classes.draft}>
              <Icon>construction</Icon> Not published
            </Box>
          )}

          {currentDay && (
            <>
              <IconButton
                size={'small'}
                aria-label="program menu"
                aria-controls="menu-program"
                aria-haspopup="true"
                onClick={(event) => setProgramMenuEl(event.currentTarget)}
                color="default"
              >
                <Icon>event</Icon>
              </IconButton>
              <Menu
                id="menu-program"
                anchorEl={programMenuEl}
                elevation={0}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(programMenuEl)}
                onClose={() => setProgramMenuEl(null)}
                slotProps={{ paper: { sx: classes.menuBg } }}
              >
                {totalFavorites.length > 0 && (
                  <Box sx={classes.addToCal}>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<Icon>event</Icon>}
                      target="_blank"
                      href={`/api/visitor/conferences/${conferenceDetail.id}/downloadfavorites`}
                    >
                      {window.LANG.ADD_TO_CAL} ({totalFavorites.length})
                    </Button>
                  </Box>
                )}

                <ProgramViewGrid
                  // @ts-expect-error: React.memo type error
                  programDay={currentDay}
                  programDayId={currentDay.id}
                  totalFavorites={totalFavorites}
                  updateStepper={conferenceUpdateStepper}
                />
              </Menu>
            </>
          )}
          {currentLiveCount > 0 && (
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                aria-label="Menu open"
                sx={classes.videoIcon}
                onClick={() => setMenuOpen(true)}
              >
                <Icon>videocam</Icon>
                <span style={classes.videoIconCount}>{currentLiveCount}</span>
              </IconButton>
            </Box>
          )}
        </Box>
        {/*  )}*/}
        <Box sx={classes.title}>{props.header}</Box>
        {authState.stateLoaded && authState.user ? (
          <Box display="flex" alignItems="center">
            {props.extraTopButtons}
            <Box>
              <IconButton
                size={smallScreen ? 'small' : 'medium'}
                aria-label="Connections"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleFollowMenu}
                color="default"
                variant="contained"
              >
                <Badge
                  color="primary"
                  badgeContent={authState.user.follows_new}
                >
                  <Icon sx={classes.peopleButton}>contact_mail</Icon>
                </Badge>
              </IconButton>
              <FollowMenu ref={followMenuRef} />
            </Box>
            <Box>
              <IconButton
                size={buttonSize}
                aria-label="Connections"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleThreadMenu}
                color="default"
                variant="contained"
                disableRipple={true}
              >
                <RippleBadge
                  color="primary"
                  badgeContent={unreadChats + unreadNotifications}
                >
                  <Icon sx={classes.peopleButton}>forum</Icon>
                </RippleBadge>
              </IconButton>
              <ThreadMenu ref={threadMenuRef} />
            </Box>
            <Box>
              <IconButton
                size={smallScreen ? 'small' : 'medium'}
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="default"
              >
                <UserAvatar size="small" user={authState.user} />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                elevation={0}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
                slotProps={{ paper: { sx: classes.menuBg } }}
              >
                <MenuItem onClick={account}>Profile</MenuItem>
                <MenuItem onClick={editProfile}>Edit Profile</MenuItem>
                <MenuItem onClick={editProfileNotifications}>
                  Edit Notifications
                </MenuItem>
                <MenuItem onClick={logout}>Logout</MenuItem>
              </Menu>
            </Box>
          </Box>
        ) : (
          <Box display="flex" alignItems="center">
            {location.pathname !== '/' && (
              <>
                <Typography sx={classes.loginLinkText} variant="body2" noWrap>
                  Already registered?
                </Typography>
                <Link sx={classes.loginLink} href="/">
                  Login
                </Link>
              </>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default VisitorNav;
